import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, Volume2, VolumeX } from 'lucide-react';
import { colors, spacing, shadows, transitions, easings, typography } from '../../styles/designSystem';
import { useSoundContext } from '../../contexts/SoundContext';

const NavContainer = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(254, 254, 254, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${colors.sacred.mist};
  transition: ${transitions.sacred};
  
  @media (max-width: 768px) {
    background: ${colors.sacred.white};
    backdrop-filter: none;
    box-shadow: ${shadows.soft};
  }
  
  @supports not (backdrop-filter: blur(20px)) {
    background: ${colors.sacred.white};
  }
`;

const NavContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${spacing[4]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 72px;
  
  @media (min-width: 640px) {
    padding: 0 ${spacing[8]};
  }
`;

const Logo = styled(motion(Link))`
  display: flex;
  align-items: center;
  text-decoration: none;
  font-family: ${typography.fonts.display};
  font-size: ${typography.sizes['2xl']};
  font-weight: ${typography.weights.bold};
  color: ${colors.sacred.night};
  transition: ${transitions.gentle};
  line-height: 1;
  
  &:hover {
    color: ${colors.tiffany[600]};
  }
`;

const LogoImage = styled.img`
  height: 65px;
  width: auto;
  transition: ${transitions.gentle};
  display: block;
`;

const DesktopNav = styled.div`
  display: none;
  align-items: center;
  gap: ${spacing[6]};
  flex: 1;
  justify-content: center;
  
  @media (min-width: 900px) {
    display: flex;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[6]};
  white-space: nowrap;
`;

const NavLink = styled(Link)`
  position: relative;
  text-decoration: none;
  font-weight: ${typography.weights.medium};
  font-size: ${typography.sizes.base};
  color: ${colors.sacred.shadow};
  transition: ${transitions.gentle};
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${spacing[2]};
  white-space: nowrap;
  
  &:hover {
    color: ${colors.tiffany[600]};
    background: ${colors.tiffany[50]};
  }
  
  &.active {
    color: ${colors.tiffany[600]};
    
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 50%;
      width: 6px;
      height: 6px;
      background: ${colors.primary[500]};
      border-radius: 50%;
      transform: translateX(-50%);
    }
  }
`;

const UtilityButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
`;

const LanguageToggle = styled.div`
  position: relative;
`;

const LanguageButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  background: none;
  border: 1px solid ${colors.sacred.stone};
  border-radius: ${spacing[6]};
  padding: ${spacing[2]} ${spacing[3]};
  font-size: ${typography.sizes.sm};
  font-weight: ${typography.weights.medium};
  color: ${colors.sacred.shadow};
  cursor: pointer;
  transition: ${transitions.gentle};
  
  &:hover {
    border-color: ${colors.primary[400]};
    color: ${colors.tiffany[600]};
    background: ${colors.tiffany[50]};
  }
`;

const LanguageDropdown = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${spacing[2]};
  background: ${colors.sacred.white};
  border: 1px solid ${colors.sacred.mist};
  border-radius: ${spacing[3]};
  box-shadow: ${shadows.flowing};
  overflow: hidden;
  min-width: 120px;
`;

const LanguageOption = styled.button`
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: ${spacing[3]} ${spacing[4]};
  font-size: ${typography.sizes.sm};
  color: ${colors.sacred.shadow};
  cursor: pointer;
  transition: ${transitions.gentle};
  
  &:hover {
    background: ${colors.primary[50]};
    color: ${colors.tiffany[600]};
  }
  
  &.active {
    background: ${colors.primary[100]};
    color: ${colors.primary[700]};
    font-weight: ${typography.weights.medium};
  }
`;

const SoundToggle = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: none;
  border: 1px solid ${colors.sacred.stone};
  border-radius: 50%;
  color: ${colors.sacred.shadow};
  cursor: pointer;
  transition: ${transitions.gentle};
  
  &:hover {
    border-color: ${colors.primary[400]};
    color: ${colors.tiffany[600]};
    background: ${colors.tiffany[50]};
  }
  
  &.active {
    border-color: ${colors.primary[500]};
    color: ${colors.tiffany[600]};
    background: ${colors.primary[50]};
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  color: ${colors.sacred.shadow};
  cursor: pointer;
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 72px;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.sacred.white};
  backdrop-filter: none;
  z-index: 9998;
  padding: ${spacing[6]};
  box-shadow: ${shadows.soft};
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[8]};
`;

const MobileNavLink = styled(Link)`
  text-decoration: none;
  font-weight: ${typography.weights.medium};
  font-size: ${typography.sizes.lg};
  color: ${colors.sacred.shadow};
  padding: ${spacing[4]};
  border-radius: ${spacing[3]};
  transition: ${transitions.gentle};
  text-align: center;
  
  &:hover {
    color: ${colors.tiffany[600]};
    background: ${colors.tiffany[50]};
  }
  
  &.active {
    color: ${colors.tiffany[600]};
    background: ${colors.primary[50]};
  }
`;

const Navigation = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { isSoundEnabled, toggleSound } = useSoundContext();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Only show sound toggle on homepage
  const isHomepage = location.pathname === '/';

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsLanguageDropdownOpen(false);
  }, [location]);

  const languages = [
    { code: 'en', name: 'English', flag: 'EN' },
    { code: 'es', name: 'Español', flag: 'ES' },
    // { code: 'fr', name: 'Français', flag: '🇫🇷' }
  ];

  const navItems = [
    { to: '/', label: t('nav.home') },
    { to: '/treatments', label: t('nav.treatments') },
    { to: '/wellness-quiz', label: t('nav.wellnessQuiz') },
    { to: '/journal', label: t('nav.blog') },
    { to: '/contact', label: t('nav.contact') }
  ];

  const isActiveRoute = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsLanguageDropdownOpen(false);
  };



  return (
    <NavContainer
      animate={{ 
        backgroundColor: isScrolled 
          ? 'rgba(254, 254, 254, 0.98)' 
          : 'rgba(254, 254, 254, 0.95)',
        borderBottomColor: isScrolled 
          ? colors.sacred.stone 
          : colors.sacred.mist
      }}
      transition={{ duration: 0.3, ease: easings.gentle }}
    >
      <NavContent>
        <Logo to="/" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <LogoImage src="/images/logo.jpg" alt="Ama Wellness & Spa Logo" />
        </Logo>

        <DesktopNav>
          <NavLinks>
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={isActiveRoute(item.to) ? 'active' : ''}
              >
                {item.label}
              </NavLink>
            ))}
          </NavLinks>

          <UtilityButtons>
            {isHomepage && (
              <SoundToggle
                onClick={toggleSound}
                className={isSoundEnabled ? 'active' : ''}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title={t('hero.toggleSound')}
              >
                {isSoundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
              </SoundToggle>
            )}

            <LanguageToggle>
              <LanguageButton
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Globe size={16} />
                {languages.find(lang => lang.code === i18n.language)?.flag}
              </LanguageButton>

              <AnimatePresence>
                {isLanguageDropdownOpen && (
                  <LanguageDropdown
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: easings.gentle }}
                  >
                    {languages.map((lang) => (
                      <LanguageOption
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={i18n.language === lang.code ? 'active' : ''}
                      >
                        {lang.flag} {lang.name}
                      </LanguageOption>
                    ))}
                  </LanguageDropdown>
                )}
              </AnimatePresence>
            </LanguageToggle>


          </UtilityButtons>
        </DesktopNav>

        <MobileMenuButton
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </MobileMenuButton>
      </NavContent>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: easings.gentle }}
          >
            <MobileNavLinks>
              {navItems.map((item) => (
                <MobileNavLink
                  key={item.to}
                  to={item.to}
                  className={isActiveRoute(item.to) ? 'active' : ''}
                >
                  {item.label}
                </MobileNavLink>
              ))}

            </MobileNavLinks>

            <UtilityButtons style={{ justifyContent: 'center' }}>
              {isHomepage && (
                <SoundToggle
                  onClick={toggleSound}
                  className={isSoundEnabled ? 'active' : ''}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isSoundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
                </SoundToggle>
              )}

              <LanguageToggle>
                <LanguageButton
                  onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Globe size={16} />
                  {languages.find(lang => lang.code === i18n.language)?.flag}
                </LanguageButton>

                <AnimatePresence>
                  {isLanguageDropdownOpen && (
                    <LanguageDropdown
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2, ease: easings.gentle }}
                    >
                      {languages.map((lang) => (
                        <LanguageOption
                          key={lang.code}
                          onClick={() => changeLanguage(lang.code)}
                          className={i18n.language === lang.code ? 'active' : ''}
                        >
                          {lang.flag} {lang.name}
                        </LanguageOption>
                      ))}
                    </LanguageDropdown>
                  )}
                </AnimatePresence>
              </LanguageToggle>
            </UtilityButtons>
          </MobileMenu>
        )}
      </AnimatePresence>
    </NavContainer>
  );
};

export default Navigation; 
